import { useCallback, useState } from "react";

/**
 * Custom hook to manage session storage with React state.
 *
 * This hook provides a way to store and retrieve values from the session storage,
 * while keeping the state in sync with React's state management.
 *
 * @template T - The type of the value to be stored.
 * @param {string} key - The key under which the value is stored in session storage.
 * @param {T} [initialValue] - Optional initial value to be used if there is no value in session storage.
 * @returns {[T | undefined, (newValue: T) => void]} - A tuple containing the stored value and a function to update it.
 */
export function useSessionStorage<T>(
  key: string,
  initialValue?: T
): [T | undefined, (newValue: T) => void] {
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    const saved = sessionStorage.getItem(key);
    if (saved != null) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialValue;
      }
    }
    return initialValue;
  });

  const setValue = useCallback(
    (newValue: T) => {
      setStoredValue(newValue);
      sessionStorage.setItem(key, JSON.stringify(newValue));
    },
    [key]
  );

  return [storedValue, setValue];
}
